import React from "react";
import QRCode from "qrcode";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getSuccessContainedDarkTextSx } from "./claimActionStyles";
import ClaimFixedAction from "./ClaimFixedAction";

function useQrDataUrl(value = "", width = 420) {
  const [qrDataUrl, setQrDataUrl] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      setQrDataUrl("");
      return () => {
        cancelled = true;
      };
    }

    QRCode.toDataURL(normalizedValue, {
      errorCorrectionLevel: "M",
      margin: 1,
      width,
    })
      .then((nextUrl) => {
        if (!cancelled) setQrDataUrl(nextUrl);
      })
      .catch((error) => {
        console.warn("Unable to generate redemption QR code:", error);
        if (!cancelled) setQrDataUrl("");
      });

    return () => {
      cancelled = true;
    };
  }, [value, width]);

  return qrDataUrl;
}

function QrImage({ src, alt, size, testId }) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        p: 0.75,
        bgcolor: "#fff",
        borderRadius: 1.25,
        boxShadow: "0 10px 24px rgba(15,23,42,0.16)",
      }}
    >
      {src ? (
        <Box
          component="img"
          data-testid={testId}
          src={src}
          alt={alt}
          sx={{ display: "block", width: size, height: size }}
        />
      ) : (
        <Box
          data-testid={testId}
          aria-label={alt}
          sx={{
            width: size,
            height: size,
            borderRadius: 0.75,
            bgcolor: "grey.100",
          }}
        />
      )}
    </Box>
  );
}

export default function ClaimRedeemQrAction({
  redeemUrl = "",
  actionLabel = "Scan to redeem",
  helperText = "Tap to enlarge",
  dialogTitle = "Redemption QR code",
}) {
  const [open, setOpen] = React.useState(false);
  const compactQrUrl = useQrDataUrl(redeemUrl, 180);
  const expandedQrUrl = useQrDataUrl(redeemUrl, 420);

  if (!String(redeemUrl || "").trim()) return null;

  return (
    <>
      <ClaimFixedAction maxWidth={600}>
        <Box
          component="button"
          type="button"
          data-testid="ticket-fixed-action"
          onClick={() => setOpen(true)}
          sx={(theme) => ({
            width: "100%",
            m: 0,
            p: 1,
            border: 0,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            textAlign: "left",
            cursor: "pointer",
            color: "text.primary",
            bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.16 : 0.09),
            "&:focus-visible": {
              outline: `3px solid ${alpha(theme.palette.success.main, 0.34)}`,
              outlineOffset: 2,
            },
          })}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              {actionLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {helperText}
            </Typography>
          </Box>
          <QrImage
            src={compactQrUrl}
            alt="Redemption QR code"
            size={68}
            testId="ticket-compact-qr"
          />
        </Box>
      </ClaimFixedAction>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <Box data-testid="ticket-expanded-qr-dialog">
          <DialogTitle sx={{ pb: 1 }}>{dialogTitle}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} alignItems="center" sx={{ textAlign: "center", pb: 1 }}>
              <Typography color="text.secondary">
                Ready for staff to scan.
              </Typography>
              <QrImage
                src={expandedQrUrl || compactQrUrl}
                alt="Expanded redemption QR code"
                size={260}
              />
              <Button
                variant="contained"
                fullWidth
                onClick={() => setOpen(false)}
                sx={(theme) => getSuccessContainedDarkTextSx(theme)}
              >
                Done
              </Button>
            </Stack>
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
}
